import {
  TRANSFORMATION_STATUS_LABELS,
  type StockTransformation,
} from "@artisancode/api-types";
import { Calendar, CheckCircle2, Eye, Scale, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateWIB } from "@/lib/date-utils";

import { summarizeRepacking } from "./repacking-utils";

import type { RepackingRowsProps } from "./repacking-desktop-table";

export function RepackingMobileList({ rows, ...actions }: RepackingRowsProps) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-xs text-muted-foreground">
        Tidak ada dokumen repacking yang sesuai kriteria.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((item) => (
        <RepackingCard key={item.id} item={item} {...actions} />
      ))}
    </div>
  );
}

function RepackingCard({
  item,
  canComplete,
  isCompleting,
  onViewDetail,
  onComplete,
  onCancelRequest,
}: Omit<RepackingRowsProps, "rows"> & { item: StockTransformation }) {
  const statusMeta = TRANSFORMATION_STATUS_LABELS[item.status];
  const { inputLine, outputLine, inputUom, outputUom, shrinkagePercent } =
    summarizeRepacking(item);
  const isDraft = item.status === "draft";

  return (
    <Card className="gap-2 py-3 shadow-xs">
      <CardContent className="space-y-2.5 px-4 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="font-mono text-sm font-bold text-foreground">
              {item.code}
            </span>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {formatDateWIB(item.date)}
              {item.createdByName && ` • ${item.createdByName}`}
            </p>
          </div>
          <Badge
            variant={statusMeta.variant}
            className="shrink-0 text-[10px] capitalize"
          >
            {statusMeta.label}
          </Badge>
        </div>

        <div className="space-y-1 rounded-md bg-muted/40 p-2.5">
          <span className="block text-[11px] font-medium text-muted-foreground">
            Bahan Baku Dibuka (Segel → Bar):
          </span>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">
              {inputLine?.itemName || "Item"}
            </span>
            <span className="font-mono text-xs font-bold text-amber-600 dark:text-amber-400">
              {item.totalInputQty.toLocaleString("id-ID")} {inputUom}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded border p-2">
            <span className="block text-[10px] text-muted-foreground">
              Masuk Bar{outputLine ? `: ${outputLine.itemName}` : ""}
            </span>
            <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
              {item.totalOutputQty.toLocaleString("id-ID")} {outputUom}
            </span>
          </div>
          <div className="rounded border p-2">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Scale className="h-3 w-3 text-rose-500" />
              Susut Proses
            </span>
            <span className="font-mono font-semibold text-rose-600 dark:text-rose-400">
              {item.shrinkageQty.toLocaleString("id-ID")} {outputUom} (
              {shrinkagePercent}%)
            </span>
          </div>
        </div>

        {item.note && (
          <p className="rounded bg-muted/20 p-2 text-[11px] italic text-muted-foreground">
            {item.note}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border/40 pt-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetail(item)}
            className="min-h-[40px] gap-1.5 text-xs"
          >
            <Eye className="h-3.5 w-3.5" />
            Rincian
          </Button>

          {isDraft && canComplete && (
            <Button
              size="sm"
              onClick={() => onComplete(item.id)}
              disabled={isCompleting}
              className="min-h-[40px] gap-1.5 bg-emerald-600 text-xs text-white hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Selesaikan
            </Button>
          )}

          {isDraft && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancelRequest(item)}
              className="min-h-[40px] text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <XCircle className="mr-1 h-3.5 w-3.5" />
              Batalkan
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
